import React, { useState, useRef } from 'react';
import Modal from 'react-modal';
import { translate } from 'javascripts/translations/i18n';
import apiClient from 'apiClient';
import { ActionLink } from 'components/shared';
import Spinner from 'components/core/Spinner';
import { Button } from '@companycam/slab-web';

type DownloadModalProps = {
  isOpen: boolean;
  user: { email_address: string };
  photos: number[];
  onRequestClose: () => void;
  showVideoAlert?: boolean;
};

const DownloadModal = ({
  user,
  onRequestClose,
  photos,
  showVideoAlert = false,
  isOpen,
}: DownloadModalProps) => {
  const [email, setEmail] = useState(user?.email_address ?? '');
  const [completed, setCompleted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uri, setUri] = useState('');
  const checkTimeoutId = useRef<NodeJS.Timer>();

  const fetchDownload = (slug: string) => {
    return apiClient.get(`images/downloads/${slug}`);
  };

  const closeModal = () => {
    if (checkTimeoutId.current) {
      clearTimeout(checkTimeoutId.current);
    }

    setCompleted(false);
    setSubmitted(false);

    onRequestClose();
  };

  const checkStatus = (slug: string) => {
    fetchDownload(slug).then((download) => {
      if (download.status === 'completed') {
        if (checkTimeoutId.current) {
          clearTimeout(checkTimeoutId.current);
        }

        setCompleted(true);
        setUri(download.uri);
      } else {
        checkTimeoutId.current = setTimeout(() => {
          checkStatus(slug);
        }, 2000);
      }
    });
  };

  const downloadPhotos = (annotated: boolean) => {
    const params = {
      email,
      image_ids: photos,
      annotated,
    };
    const path = 'images/downloads';

    apiClient.post(path, params).then((download) => {
      checkTimeoutId.current = setTimeout(() => {
        checkStatus(download.slug);
      }, 2000);
    });

    setSubmitted(true);

    return false;
  };

  const renderVideoAlert = () => {
    if (!showVideoAlert) {
      return null;
    }

    const videoMessage =
      photos?.length > 0
        ? translate('photos.downloadModal.videoMessage.withPhotos', {
            count: photos.length,
          })
        : translate('photos.downloadModal.videoMessage.withoutPhotos');

    return <span className="instructions">{videoMessage}</span>;
  };

  const renderForm = () => {
    if (submitted) {
      return null;
    }

    return (
      <>
        <div>
          <h3 className="modal-title">
            {translate('photos.downloadModal.pendingTitle')}
          </h3>
          {renderVideoAlert()}
          {photos.length > 0 && (
            <span className="instructions">
              {translate('photos.downloadModal.instructions')}
            </span>
          )}
        </div>
        {photos.length > 0 && (
          <>
            <span>
              <label htmlFor="email">{translate('common.emailAddress')}</label>
              <input
                type="text"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
            </span>
            <div className="btn-container full">
              <Button color="primary" onClick={() => downloadPhotos(false)}>
                {translate('common.downloadOriginal')}
              </Button>
              <Button color="primary" onClick={() => downloadPhotos(true)}>
                {translate('common.downloadModified')}
              </Button>
            </div>
          </>
        )}
      </>
    );
  };

  const renderProgress = () => {
    if (!submitted) {
      return null;
    }

    return (
      <div>
        <h3 className="modal-title">
          {translate('photos.downloadModal.packagingTitle')}
          <Spinner large />
        </h3>
        <span className="instructions">
          {email?.length
            ? translate('photos.downloadModal.packagingInstructionsEmail', {
                email,
              })
            : translate('photos.downloadModal.packagingInstructionsNoEmail')}
        </span>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={closeModal}
      contentLabel={translate('photos.downloadModal.downloadPhotos')}
      overlayClassName="modal"
      className="modal-content-wrap"
    >
      <ActionLink className="modal-close-button" onClick={onRequestClose}>
        <i className="mdi mdi-close" />
      </ActionLink>
      <div>
        {completed ? (
          <div className="modal-success-action">
            <div className="modal-title">
              {translate('photos.downloadModal.completedTitle')}
            </div>
            <div className="btn-container full">
              <Button
                color="primary"
                href={uri}
                target="_blank"
                rel="noopener noreferrer"
                icon={{ name: 'download' }}
              >
                {translate('photos.downloadModal.downloadPhotos')}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {renderForm()}
            {renderProgress()}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DownloadModal;
